/**
 * Telemetry readers — `/health` and `/metrics`.
 *
 * Server contracts (see `crates/gi-api-server/src/routes/health.rs`):
 *
 *   GET /health
 *       200 OK       when every check is healthy or degraded
 *       503          when any check is unhealthy
 *       body:        { status, version, checks }
 *
 *   GET /metrics
 *       200 OK       Prometheus text-exposition format (version 0.0.4)
 *
 * Both functions are **tolerant of being called without a server**:
 * mock-mode UIs still mount the Command dashboard, and a failed
 * fetch returns `null` rather than throwing. The caller (a TanStack
 * Query hook) surfaces the null as a "not connected" tile in the UI.
 *
 * The Prometheus parser here is intentionally minimal — it extracts
 * counter + gauge values by name, histogram `_bucket` + `_sum` +
 * `_count` triples, and computes p50 / p95 from bucket counts via
 * linear interpolation between the two surrounding buckets. It does
 * NOT attempt to be a general Prometheus client; it is a read-only
 * convenience for the dashboard UI.
 */

import { apiBaseUrl } from '../http';
import type { EngineHealth, EngineMetrics, HistogramSummary } from '../types';

/** Fetch + parse `/health`. Returns `null` on network error or an
 *  unparseable body — the dashboard renders that state as "offline". */
export async function fetchEngineHealth(): Promise<EngineHealth | null> {
  const url = `${apiBaseUrl()}/health`;
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
    });
    const raw = await res.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    const status = typeof obj['status'] === 'string' ? (obj['status'] as string) : '';
    const version = typeof obj['version'] === 'string' ? (obj['version'] as string) : '';
    const checks = Array.isArray(obj['checks']) ? obj['checks'] : [];
    return {
      status: normaliseStatus(status),
      version,
      checks: checks.map(coerceCheckOutcome),
      http_status: res.status,
      raw_status: status,
    };
  } catch {
    return null;
  }
}

/** Fetch + parse `/metrics`. Returns `null` on network error. */
export async function fetchEngineMetrics(): Promise<EngineMetrics | null> {
  const url = `${apiBaseUrl()}/metrics`;
  try {
    const res = await fetch(url, { headers: { Accept: 'text/plain' } });
    if (!res.ok) return null;
    const body = await res.text();
    return parsePrometheus(body);
  } catch {
    return null;
  }
}

// ─── Prometheus parser ───────────────────────────────────────────

/** Parse Prometheus text-exposition format. Tolerates malformed
 *  lines (skips them) so a half-written scrape does not blow up the
 *  UI. */
export function parsePrometheus(text: string): EngineMetrics {
  const scalars: Record<string, number> = {};
  const histograms: Record<string, HistogramBuckets> = {};

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line === '' || line.startsWith('#')) continue;

    // Split "name{labels}" "value" — labels are optional.
    const match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)(\{[^}]*\})?\s+(\S+)$/);
    if (!match) continue;
    const name = match[1]!;
    const labels = parseLabels(match[2] ?? '');
    const value = Number(match[3]!);
    if (!Number.isFinite(value)) continue;

    // Histogram triple: `<name>_bucket` / `<name>_sum` / `<name>_count`.
    if (name.endsWith('_bucket')) {
      const base = name.slice(0, -'_bucket'.length);
      const bucket = (histograms[base] ??= emptyBuckets());
      const le = Number(labels['le']);
      if (Number.isFinite(le) || labels['le'] === '+Inf') {
        bucket.buckets.push({ le: labels['le'] === '+Inf' ? Infinity : le, count: value });
      }
      continue;
    }
    if (name.endsWith('_sum')) {
      const base = name.slice(0, -'_sum'.length);
      const bucket = (histograms[base] ??= emptyBuckets());
      bucket.sum = value;
      continue;
    }
    if (name.endsWith('_count')) {
      const base = name.slice(0, -'_count'.length);
      const bucket = (histograms[base] ??= emptyBuckets());
      bucket.count = value;
      continue;
    }

    scalars[name] = value;
  }

  const histogramSummaries: Record<string, HistogramSummary> = {};
  for (const [name, bucket] of Object.entries(histograms)) {
    histogramSummaries[name] = summariseHistogram(bucket);
  }

  return { scalars, histograms: histogramSummaries };
}

interface HistogramBuckets {
  /** Cumulative bucket counts — `buckets[i].count` is the number of
   *  observations `≤ buckets[i].le`. Stored unsorted; sorted at
   *  summary time. */
  buckets: Array<{ le: number; count: number }>;
  sum: number;
  count: number;
}

function emptyBuckets(): HistogramBuckets {
  return { buckets: [], sum: 0, count: 0 };
}

function parseLabels(raw: string): Record<string, string> {
  if (!raw) return {};
  const inside = raw.slice(1, -1); // strip { }
  const out: Record<string, string> = {};
  for (const pair of splitLabelPairs(inside)) {
    const eq = pair.indexOf('=');
    if (eq <= 0) continue;
    const k = pair.slice(0, eq).trim();
    const v = pair
      .slice(eq + 1)
      .trim()
      .replace(/^"|"$/g, '');
    out[k] = v;
  }
  return out;
}

/** Split `foo="a",bar="b,c"` on commas that are NOT inside quotes. */
function splitLabelPairs(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') depth = depth === 0 ? 1 : 0;
    else if (ch === ',' && depth === 0) {
      out.push(s.slice(start, i));
      start = i + 1;
    }
  }
  out.push(s.slice(start));
  return out.filter(Boolean);
}

function summariseHistogram(h: HistogramBuckets): HistogramSummary {
  const sorted = [...h.buckets].sort((a, b) => a.le - b.le);
  return {
    count: h.count,
    sum: h.sum,
    p50_seconds: percentileFromBuckets(sorted, 0.5),
    p95_seconds: percentileFromBuckets(sorted, 0.95),
  };
}

/** Linear interpolation between the buckets that straddle the target
 *  quantile. Returns `NaN` if the histogram has zero observations. */
function percentileFromBuckets(
  sorted: Array<{ le: number; count: number }>,
  q: number,
): number {
  if (sorted.length === 0) return Number.NaN;
  const total = sorted[sorted.length - 1]!.count;
  if (total === 0) return Number.NaN;
  const target = q * total;
  let prevLe = 0;
  let prevCount = 0;
  for (const { le, count } of sorted) {
    if (count >= target) {
      if (!Number.isFinite(le)) return prevLe;
      const spanCount = count - prevCount;
      if (spanCount <= 0) return le;
      const frac = (target - prevCount) / spanCount;
      return prevLe + (le - prevLe) * frac;
    }
    prevLe = Number.isFinite(le) ? le : prevLe;
    prevCount = count;
  }
  return Number.isFinite(sorted[sorted.length - 1]!.le)
    ? sorted[sorted.length - 1]!.le
    : prevLe;
}

// ─── Health helpers ──────────────────────────────────────────────

function normaliseStatus(raw: string): EngineHealth['status'] {
  const lower = raw.toLowerCase();
  if (lower === 'healthy' || lower === 'degraded' || lower === 'unhealthy') {
    return lower;
  }
  return 'unknown';
}

function coerceCheckOutcome(v: unknown): EngineHealth['checks'][number] {
  if (typeof v !== 'object' || v === null) {
    return { name: 'unknown', status: 'unknown', detail: '' };
  }
  const o = v as Record<string, unknown>;
  return {
    name: typeof o['name'] === 'string' ? (o['name'] as string) : 'unknown',
    status: normaliseStatus(
      typeof o['status'] === 'string' ? (o['status'] as string) : '',
    ),
    detail: typeof o['detail'] === 'string' ? (o['detail'] as string) : '',
  };
}
